import { useState, useMemo } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  message,
  Popconfirm,
  Card,
  Empty,
  Tabs,
  Tooltip,
  Typography,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  EditOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { featureAssignmentsApi, type FeatureAssignment } from '@/api/feature-assignments'
import { FeatureAssignmentModal } from '../features/FeatureAssignmentModal'
import { FeatureEditModal } from '../features/FeatureEditModal'
import { getApiErrorMessage } from '@/utils/error'

const { Text } = Typography

interface HospitalFeaturesProps {
  hospitalId: string
}

export const HospitalFeatures = ({ hospitalId }: HospitalFeaturesProps) => {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState<FeatureAssignment | null>(null)
  const queryClient = useQueryClient()

  // Fetch assigned features
  const { data: assignedData, isLoading } = useQuery({
    queryKey: ['feature-assignments', hospitalId],
    queryFn: () => featureAssignmentsApi.getAssignedFeatures(hospitalId),
  })

  const unassignMutation = useMutation({
    mutationFn: (featureId: string) => featureAssignmentsApi.unassignFeature(hospitalId, featureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-assignments', hospitalId] })
      queryClient.invalidateQueries({ queryKey: ['hospital-features', hospitalId] })
      queryClient.invalidateQueries({ queryKey: ['hospital', hospitalId] })
      message.success('Feature unassigned successfully')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to unassign feature'))
    },
  })

  const handleUnassign = (featureId: string) => {
    unassignMutation.mutate(featureId)
  }

  const handleEdit = (assignment: FeatureAssignment) => {
    setSelectedAssignment(assignment)
    setIsEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setIsEditModalOpen(false)
    setSelectedAssignment(null)
  }

  const assignedFeatures = assignedData?.data || []
  const assignedFeatureIds = new Set(assignedFeatures.map((a) => a.featureId))

  // Precompute assignment map for O(1) lookups in table render (avoids O(n²))
  const assignmentMap = useMemo(
    () => new Map(assignedFeatures.map((a) => [a.id, a])),
    [assignedFeatures]
  )

  // Format value for display
  const formatValue = (value: unknown, dataType: string) => {
    if (value === null || value === undefined) {
      return <Text type="secondary">—</Text>
    }

    switch (dataType) {
      case 'boolean':
        return <Tag color={value ? 'success' : 'default'}>{String(value)}</Tag>

      case 'string':
        return <Tag color="green">{String(value)}</Tag>

      case 'number':
        return <Tag color="cyan">{String(value)}</Tag>

      case 'json':
        return (
          <Tooltip
            title={
              <pre style={{ margin: 0, maxHeight: 300, overflow: 'auto' }}>
                {JSON.stringify(value, null, 2)}
              </pre>
            }
          >
            <Tag color="purple" style={{ cursor: 'pointer' }}>
              JSON Object <InfoCircleOutlined />
            </Tag>
          </Tooltip>
        )

      default:
        return <Tag>{String(value)}</Tag>
    }
  }

  interface AssignmentRow {
    key: string
    id: string
    featureId: string
    featureKey: string
    featureName: string
    category: string
    storageLocation: string
    dataType: string
    enabled: boolean
    value: unknown
  }

  const tableData: AssignmentRow[] = assignedFeatures.map((assignment: FeatureAssignment) => ({
    key: assignment.id,
    id: assignment.id,
    featureId: assignment.featureId,
    featureKey: assignment.feature?.key || 'unknown',
    featureName: assignment.feature?.name || 'Unknown Feature',
    category: '—', // Category will need to be populated via a join or separate lookup
    storageLocation: assignment.feature?.storage_location || '—',
    dataType: assignment.feature?.data_type || 'unknown',
    enabled: assignment.enabled,
    value: assignment.value,
  }))

  const columns = [
    {
      title: 'Feature',
      dataIndex: 'featureName',
      key: 'featureName',
      render: (text: string, record: AssignmentRow) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <code>{record.featureKey}</code>
          </Text>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
    },
    {
      title: 'Storage',
      dataIndex: 'storageLocation',
      key: 'storageLocation',
      width: 150,
      render: (location: string) => <Tag>{location}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'dataType',
      key: 'dataType',
      width: 100,
      render: (type: string) => {
        const colors: Record<string, string> = {
          boolean: 'blue',
          string: 'green',
          number: 'cyan',
          json: 'purple',
        }
        return <Tag color={colors[type] || 'default'}>{type}</Tag>
      },
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      render: (enabled: boolean) => (
        <Tag
          color={enabled ? 'success' : 'default'}
          icon={enabled ? <CheckCircleOutlined /> : undefined}
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: unknown, record: AssignmentRow) => formatValue(value, record.dataType),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: AssignmentRow) => {
        const assignment = assignmentMap.get(record.id)
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => assignment && handleEdit(assignment)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Unassign Feature"
              description={`Are you sure you want to unassign "${record.featureName}"?`}
              onConfirm={() => handleUnassign(record.featureId)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Remove
              </Button>
            </Popconfirm>
          </Space>
        )
      },
    },
  ]

  return (
    <Card
      title="Hospital Features"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAssignModalOpen(true)}>
          Assign Feature
        </Button>
      }
    >
      <Tabs
        items={[
          {
            key: 'predefined',
            label: 'Predefined Features',
            children:
              tableData.length === 0 ? (
                <Empty description="No features assigned to this hospital">
                  <Button type="primary" onClick={() => setIsAssignModalOpen(true)}>
                    Assign Feature
                  </Button>
                </Empty>
              ) : (
                <Table
                  dataSource={tableData}
                  columns={columns}
                  loading={isLoading}
                  pagination={false}
                  size="small"
                />
              ),
          },
        ]}
      />

      <FeatureAssignmentModal
        hospitalId={hospitalId}
        open={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        assignedFeatureIds={assignedFeatureIds}
      />

      <FeatureEditModal
        hospitalId={hospitalId}
        assignment={selectedAssignment}
        open={isEditModalOpen}
        onClose={handleEditModalClose}
      />
    </Card>
  )
}
